import React from 'react';
import {
    Group,
    GroupProps,
    BadgeVariant,
    BadgeKind,
    BadgeSize,
} from '@landr/maestro';
import useGetCurrentBreakpoint from 'hooks/useGetCurrentBreakpoint/useGetCurrentBreakpoint';
import { BaseMeta } from 'types';
import { getMetasDisplayName } from 'helpers/getMetasDisplayName/getMetasDisplayName';
import getMaxMetasAllowed from './helpers/getMaxMetasAllowed';
import getSlicedMetas from './helpers/getSlicedMetas';
import getBadges from './helpers/getBadges';

const useMetasBadge = ({
    metas,
    isLoading,
    variant,
    kind,
    size,
}: {
    metas?: BaseMeta[];
    isLoading?: boolean;
    variant: BadgeVariant;
    kind: BadgeKind;
    size: BadgeSize;
}) => {
    const currentBreakpoint = useGetCurrentBreakpoint();

    const maxMetaAllowed = getMaxMetasAllowed(currentBreakpoint, metas);
    const slicedMetas = getSlicedMetas(maxMetaAllowed, metas);
    const metasDisplayName = slicedMetas && getMetasDisplayName(slicedMetas);

    const badges = getBadges({
        metasDisplayName,
        isLoading,
        variant,
        kind,
        size,
    });

    return { badges };
};

export interface BadgeGroupProps extends GroupProps {
    metas?: BaseMeta[];
    isLoading?: boolean;
    variant?: BadgeVariant;
    kind?: BadgeKind;
    size?: BadgeSize;
}

const MetasBadge = ({
    metas,
    isLoading = false,
    variant = 'default' as BadgeVariant,
    kind = 'base' as BadgeKind,
    size = 'md' as BadgeSize,
    ...props
}: BadgeGroupProps) => {
    const { badges } = useMetasBadge({
        metas,
        isLoading,
        variant,
        kind,
        size,
    });

    if (!isLoading && !badges) {
        return null;
    }

    return (
        <Group aria-busy={isLoading} {...props}>
            {badges}
        </Group>
    );
};

export default MetasBadge;
